import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Wallet, Plus, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast, useConfirm, PageHeader, Button, Input, EmptyState, StatusBadge } from '../components/ui';
import { fmtMoney } from '../utils/currency';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const EMPTY_FORM = {
  staffId: '',
  month: now.getMonth(),
  year: now.getFullYear(),
  bonuses: '',
  deductions: '',
  notes: '',
};

const statusVariant = (status) => {
  switch (status) {
    case 'paid':     return 'success';
    case 'approved': return 'primary';
    default:         return 'neutral';
  }
};

const Payroll = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [records, setRecords] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);

  const canProcess = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canMarkPaid = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => {
    fetchRecords();
    if (canProcess) fetchStaff();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axios.get('/api/payroll', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get('/api/staff', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setStaffList(res.data);
    } catch (err) { console.error(err); }
  };

  const openModal = () => {
    setForm({ ...EMPTY_FORM, staffId: staffList[0]?._id || '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.staffId) {
      toast.error('Select a staff member');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/payroll/process', {
        staffId: form.staffId,
        month: Number(form.month),
        year: Number(form.year),
        bonuses: form.bonuses === '' ? 0 : Number(form.bonuses),
        deductions: form.deductions === '' ? 0 : Number(form.deductions),
        notes: form.notes || undefined,
      }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('Payroll processed');
      closeModal();
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error processing payroll');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkPaid = async (record) => {
    const ok = await confirm({
      title: 'Mark this payroll as paid?',
      message: `${record.staff?.user?.name || 'This staff member'} — ${fmtMoney(record.totalPay, true)} for ${MONTHS[record.month]} ${record.year}.`,
      confirmText: 'Mark paid',
    });
    if (!ok) return;
    setMarkingPaidId(record._id);
    try {
      await axios.put(`/api/payroll/${record._id}/paid`, {}, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('Marked as paid');
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error updating payroll');
    } finally {
      setMarkingPaidId(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Payroll"
        subtitle="Process monthly pay from attendance, then mark records as paid."
        action={canProcess && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={openModal}>
            Process Payroll
          </Button>
        )}
      />

      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Staff</th>
              <th>Period</th>
              <th>Hours</th>
              <th>Regular Pay</th>
              <th>Overtime</th>
              <th>Bonuses</th>
              <th>Deductions</th>
              <th>Total</th>
              <th>Status</th>
              {canMarkPaid && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 && (
              <tr>
                <td colSpan={canMarkPaid ? 10 : 9}>
                  <EmptyState
                    icon={Wallet}
                    title="No payroll records yet"
                    message={canProcess ? 'Click "Process Payroll" to generate the first one.' : 'Nothing has been processed yet.'}
                  />
                </td>
              </tr>
            )}
            {records.map(r => (
              <tr key={r._id}>
                <td className="font-medium">{r.staff?.user?.name || <span className="text-neutral-400">—</span>}</td>
                <td className="text-neutral-600 whitespace-nowrap">{MONTHS[r.month]} {r.year}</td>
                <td className="tabular-nums text-neutral-600">{r.totalHours?.toFixed(1) ?? 0}</td>
                <td className="tabular-nums">{fmtMoney(r.regularPay, true)}</td>
                <td className="tabular-nums text-neutral-600">{fmtMoney(r.overtimePay, true)}</td>
                <td className="tabular-nums text-neutral-600">{fmtMoney(r.bonuses, true)}</td>
                <td className="tabular-nums text-neutral-600">{fmtMoney(r.deductions, true)}</td>
                <td className="tabular-nums font-semibold">{fmtMoney(r.totalPay, true)}</td>
                <td>
                  <StatusBadge variant={statusVariant(r.status)} dot>{r.status}</StatusBadge>
                </td>
                {canMarkPaid && (
                  <td>
                    {r.status !== 'paid' && (
                      <Button
                        size="sm"
                        variant="success"
                        leftIcon={<CheckCircle2 size={14} />}
                        loading={markingPaidId === r._id}
                        onClick={() => handleMarkPaid(r)}
                      >
                        Mark Paid
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Process payroll modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-neutral-900">Process payroll</h2>
              <button type="button" onClick={closeModal} className="text-neutral-400 hover:text-neutral-700 transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Staff member</label>
                  {staffList.length === 0 ? (
                    <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-md p-3">
                      No staff records yet — add one on the Staff page first.
                    </p>
                  ) : (
                    <select
                      value={form.staffId}
                      onChange={(e) => setForm({ ...form, staffId: e.target.value })}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                      required
                    >
                      <option value="">Select staff…</option>
                      {staffList.map(s => (
                        <option key={s._id} value={s._id}>{s.user?.name} {s.position ? `— ${s.position}` : ''}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-neutral-700 block mb-1">Month</label>
                    <select
                      value={form.month}
                      onChange={(e) => setForm({ ...form, month: e.target.value })}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                    >
                      {MONTHS.map((m, idx) => (
                        <option key={m} value={idx}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Year"
                    type="number"
                    value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Bonuses (optional)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.bonuses}
                    onChange={(e) => setForm({ ...form, bonuses: e.target.value })}
                  />
                  <Input
                    label="Deductions (optional)"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.deductions}
                    onChange={(e) => setForm({ ...form, deductions: e.target.value })}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">Notes (optional)</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows="2"
                    className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500 resize-none"
                  />
                </div>

                <p className="text-xs text-neutral-500">
                  Pay is calculated from clock-in/clock-out hours recorded for this staff member
                  during the selected month. Re-processing the same month updates the existing record.
                </p>
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button type="button" variant="secondary" fullWidth onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth loading={submitting} disabled={staffList.length === 0}>
                  Process
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Payroll;
